import {computed, DestroyRef, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {catchError, debounceTime, EMPTY, map, Subject, switchMap, tap} from 'rxjs';
import {LabelTranslator} from '../../../core/domain/surveys/model/label-translator.model';
import {MediaService} from '../../../core/domain/surveys/scenario/media/media.service';
import {buildEmailBlock} from '../../../core/domain/surveys/scenario/media/model/build-email-block.model';
import {EmailBlock} from '../../../core/domain/surveys/scenario/media/model/email-block.model';
import {EmailBlockCard} from '../../../core/domain/surveys/scenario/media/model/email-block-card.model';
import {EmailBlockPath} from '../../../core/domain/surveys/scenario/media/model/email-block-path.model';
import {EMAIL_BLOCK_TYPE} from '../../../core/domain/surveys/scenario/media/model/email-block-type.model';
import {EmailColumn} from '../../../core/domain/surveys/scenario/media/model/email-column.model';
import {EmailContent} from '../../../core/domain/surveys/scenario/media/model/email-content.model';
import {buildEmailRow, EmailRow} from '../../../core/domain/surveys/scenario/media/model/email-row.model';
import {EmailRowOptions} from '../../../core/domain/surveys/scenario/media/model/email-row-options.model';
import {EMAIL_ROW_TYPE} from '../../../core/domain/surveys/scenario/media/model/email-row-type.model';
import {MailBuilderDeletion} from '../../../core/domain/surveys/scenario/media/model/mail-builder-deletion.model';
import {MailBuilderSelection} from '../../../core/domain/surveys/scenario/media/model/mail-builder-selection.model';
import {SurveyHeader} from '../../../core/domain/surveys/model/survey.header.model';
import {SurveyType} from '../../../core/domain/surveys/model/survey.type.model';
import {MediaEmail} from '../../../core/domain/surveys/scenario/media/model/media.email.model';

/**
 * Profondeur de l'historique. Au-delà, les pas les plus anciens tombent : garder toute la session
 * en mémoire ne servirait personne, et chaque pas est une copie du média entier.
 */
const HISTORY_DEPTH = 50;

/** Laisse le temps de faire glisser un curseur de couleur avant d'envoyer le brouillon au serveur. */
const SAVE_DRAFT_DEBOUNCE_MS = 800;

/**
 * Corps de repli, tant qu'aucun média n'est déposé. Une constante et non un `new EmailContent()` à
 * chaque lecture : une instance neuve à chaque fois relancerait sans fin ce qui en dépend.
 */
const EMPTY_CONTENT = new EmailContent();

/** Rien de sélectionné, c'est-à-dire les paramètres d'envoi. */
const SENDING_SELECTION: MailBuilderSelection = {kind: 'sending'};

/**
 * La suppression d'une section emporte la sélection qui la désignait ; les suivantes reculent d'un
 * rang.
 */
const afterRowDeletion = (selection: MailBuilderSelection, deletedIndex: number): MailBuilderSelection => {
    if (selection.kind === 'sending') return selection;

    if (selection.rowIndex === deletedIndex) return SENDING_SELECTION;

    return selection.rowIndex > deletedIndex ? {...selection, rowIndex: selection.rowIndex - 1} : selection;
};

/** La sélection suit la section déplacée, ou se décale si le déplacement l'enjambe. */
const afterRowMove = (selection: MailBuilderSelection, from: number, to: number): MailBuilderSelection => {
    if (selection.kind === 'sending') return selection;

    if (selection.rowIndex === from) return {...selection, rowIndex: to};

    if (from < to && selection.rowIndex > from && selection.rowIndex <= to) {
        return {...selection, rowIndex: selection.rowIndex - 1};
    }

    if (from > to && selection.rowIndex >= to && selection.rowIndex < from) {
        return {...selection, rowIndex: selection.rowIndex + 1};
    }

    return selection;
};

/**
 * La suppression d'un bloc emporte la sélection qui le désignait ; les blocs qui le suivaient dans
 * la même colonne reculent d'un rang.
 */
const afterBlockDeletion = (selection: MailBuilderSelection, deleted: EmailBlockPath): MailBuilderSelection => {
    if (selection.kind !== 'block') return selection;
    if (selection.rowIndex !== deleted.rowIndex || selection.columnIndex !== deleted.columnIndex) return selection;

    if (selection.blockIndex === deleted.blockIndex) return SENDING_SELECTION;

    return selection.blockIndex > deleted.blockIndex ? {...selection, blockIndex: selection.blockIndex - 1} : selection;
};

/**
 * État partagé de l'éditeur d'e-mail, fourni au niveau de la route du média.
 *
 * Le shell `MailBuilder` dépose ici le média à éditer ; l'en-tête, la palette, le canevas et le
 * panneau de réglages le relisent. Volontairement non `providedIn: 'root'` pour rester scopé à une
 * session d'édition et repartir vierge à chaque navigation.
 *
 * Toute modification remplace le média en mémoire par un média neuf : c'est ce qui rend l'historique
 * possible — chaque pas est un état complet — et ce qui permet à l'éditeur de suivre ce qu'il
 * affiche, les objets inchangés traversant la reconstruction (cf. `toEmailRow`).
 */
@Injectable()
export class MailBuilderStore {
    private readonly mediaEmailSignal = signal<MediaEmail | null>(null);

    /** Version en ligne, s'il y en a une : elle ne sert qu'à informer, jamais à être éditée. */
    private readonly publishedVersionSignal = signal<MediaEmail | null>(null);

    private readonly selectionSignal = signal<MailBuilderSelection>(SENDING_SELECTION);

    private readonly pastSignal = signal<MediaEmail[]>([]);

    private readonly futureSignal = signal<MediaEmail[]>([]);

    /**
     * Ce que la dernière modification retouchait. Deux retouches de suite du même champ ne font
     * qu'un pas d'annulation : sans cela, revenir sur un paragraphe se ferait caractère par
     * caractère.
     */
    private lastCoalesceKey: string | null = null;

    /**
     * Compte les fois où le média a changé sans qu'un panneau l'ait demandé : un nouveau dépôt, un
     * retour en arrière, un rétablissement.
     *
     * Les panneaux de réglages tiennent leur formulaire à part et le renvoient au store à chaque
     * frappe ; s'ils se réamorçaient sur le média lui-même, ils se réinitialiseraient à chacune de
     * leurs propres écritures. Ils s'accrochent donc à ce compteur, qui ne bouge que quand l'état
     * leur échappe.
     */
    private readonly stateRevisionSignal = signal(0);

    private readonly domainsSignal = signal<string[]>([]);

    /** En-tête de l'enquête : l'aperçu y puise l'adresse de collecte que le rendu doit porter. */
    private readonly surveyHeaderSignal = signal<SurveyHeader | null>(null);

    private readonly savingSignal = signal(false);

    private readonly saveFailedSignal = signal(false);

    /**
     * Ce que l'on s'apprête à retirer, tant que la question posée n'a pas reçu de réponse — `null`
     * quand rien n'attend.
     *
     * Tenu ici plutôt que dans le bouton qui a demandé : deux endroits suppriment — la barre d'une
     * section dans le canevas, le panneau de réglages pour ce qu'il règle —, et la question doit se
     * poser dans les mêmes mots des deux côtés.
     */
    private readonly pendingDeletionSignal = signal<MailBuilderDeletion | null>(null);

    private readonly draftToSave = new Subject<MediaEmail>();

    private readonly mediaService = inject(MediaService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translateService = inject(TranslateService);

    /**
     * De quoi traduire les libellés du gabarit du serveur.
     *
     * `instant` est une lecture ponctuelle, et c'est ce qu'on veut : un libellé posé devient du
     * contenu rédigé, il se fige dans la langue où il a été posé et ne suit pas les changements de
     * langue de l'interface — exactement comme s'il avait été saisi.
     */
    private readonly translateLabel: LabelTranslator = (key) => this.translateService.instant(key);

    public readonly mediaEmail = this.mediaEmailSignal.asReadonly();

    /** Ce qui fait réamorcer les formulaires des panneaux (cf. `stateRevisionSignal`). */
    public readonly stateRevision = this.stateRevisionSignal.asReadonly();

    /**
     * Domaines d'expédition que la société est autorisée à utiliser. Vide tant que le serveur ne
     * les a pas livrés : le panneau se rabat alors sur celui que l'e-mail porte déjà.
     */
    public readonly domains = this.domainsSignal.asReadonly();

    public readonly surveyHeader = this.surveyHeaderSignal.asReadonly();

    public readonly setDomains = (domains: string[]): void => this.domainsSignal.set(domains);

    public readonly selection = this.selectionSignal.asReadonly();

    /** Ce dont la suppression attend confirmation : une section, un bloc, ou rien. */
    public readonly pendingDeletion = this.pendingDeletionSignal.asReadonly();

    /** Corps de l'e-mail en cours d'édition. */
    public readonly content = computed<EmailContent>(() => this.mediaEmail()?.content ?? EMPTY_CONTENT);

    public readonly rows = computed<EmailRow[]>(() => this.content().rows);

    public readonly bodyColor = computed<string>(() => this.content().bodyColor);

    public readonly senderName = computed<string>(() => this.mediaEmail()?.senderName ?? '');

    /** Ce qui précède l'arobase : la part de l'adresse que l'utilisateur saisit. */
    public readonly senderLocalPart = computed<string>(() => this.mediaEmail()?.senderLocalPart() ?? '');

    public readonly senderDomain = computed<string>(() => this.mediaEmail()?.senderDomain() ?? '');

    public readonly object = computed<string>(() => this.mediaEmail()?.object ?? '');

    /** Section en cours d'édition, ou `null` quand la sélection ne porte pas sur une section. */
    public readonly selectedRow = computed<EmailRow | null>(() => {
        const selection = this.selection();
        return selection.kind === 'sending' ? null : (this.rows()[selection.rowIndex] ?? null);
    });

    /** Bloc en cours d'édition, ou `null` quand la sélection ne porte pas sur un bloc. */
    public readonly selectedBlock = computed<EmailBlock | null>(() => {
        const selection = this.selection();
        if (selection.kind !== 'block') return null;

        return this.content().blockAt(selection.rowIndex, selection.columnIndex, selection.blockIndex) ?? null;
    });

    /**
     * L'e-mail porte-t-il son bouton de collecte ? Il n'en emporte qu'un : la palette éteint sa
     * carte dès qu'il est posé, et son absence retient la publication.
     */
    public readonly hasCollectButton = computed<boolean>(() => this.content().hasBlockOfType(EMAIL_BLOCK_TYPE.BUTTON));

    /** L'e-mail a-t-il été publié ? */
    public readonly isPublished = computed<boolean>(() => this.mediaEmail()?.published ?? false);

    /**
     * L'e-mail ouvert ici est-il *celui* que reçoivent les destinataires ?
     *
     * Un e-mail publié cesse de l'être dès qu'on le retouche : le serveur en garde alors une copie
     * en production et rend le brouillon sur lequel on travaille. Publié sans être la version en
     * production, c'est donc exactement cela — un brouillon retouché derrière un e-mail en ligne.
     */
    public readonly isProdVersion = computed<boolean>(() => this.mediaEmail()?.prodVersion ?? false);

    /**
     * Une version est-elle servie aux destinataires alors que le brouillon a bougé depuis ? C'est
     * ce que la seconde pastille de l'en-tête annonce.
     */
    public readonly hasPublishedVersion = computed<boolean>(() => this.publishedVersionSignal() !== null);

    /**
     * Ce qui reste à faire à l'enquête elle-même, s'il y a lieu.
     *
     * Un e-mail ne mène nulle part tant que le questionnaire n'est pas en ligne : le publier
     * enverrait les répondants vers une enquête qui n'existe pas encore. Le mot suit ce que
     * l'utilisateur a sous les yeux — une enquête, ou un formulaire.
     *
     * `null` tant que l'en-tête n'est pas connu : l'éditeur n'oppose que ce qu'il voit, et un
     * chargement incomplet ne doit pas éteindre le bouton sans rien avoir à en dire.
     */
    public readonly surveyPublicationBlocker = computed<string | null>(() => {
        const surveyHeader = this.surveyHeader();
        if (!surveyHeader || surveyHeader.status === 'PUBLISHED') return null;

        return surveyHeader.type === SurveyType.FORM ? 'label.please_publish_form' : 'label.please_publish_survey';
    });

    public readonly saving = this.savingSignal.asReadonly();

    /**
     * Le dernier enregistrement a échoué. Il est différé et silencieux : sans cette pastille, la
     * modification reste à l'écran, l'utilisateur la croit acquise, et elle disparaît au
     * rechargement sans que rien ne l'ait annoncé.
     */
    public readonly saveFailed = this.saveFailedSignal.asReadonly();

    public readonly canUndo = computed<boolean>(() => this.pastSignal().length > 0);

    public readonly canRedo = computed<boolean>(() => this.futureSignal().length > 0);

    /**
     * Ce qui retient encore la diffusion de l'e-mail, sous forme de clés de traduction. Vide, il est
     * diffusable — c'est ce qui décide du bouton de publication, et ce qu'il annonce quand il est
     * éteint.
     *
     * Ce sont les clés dont le serveur se sert pour refuser une publication
     * (cf. `mediaPublicationErrorKeys`) : ce que l'éditeur voit et ce que le serveur oppose se
     * lisent alors dans les mêmes termes, sous le même bouton.
     */
    public readonly publicationBlockers = computed<string[]>(() => {
        const blockers: string[] = [];

        // En tête de liste : tant que le questionnaire n'est pas en ligne, corriger l'e-mail ne
        // servirait à rien — c'est ce qu'il faut lire en premier.
        const surveyBlocker = this.surveyPublicationBlocker();
        if (surveyBlocker) blockers.push(surveyBlocker);

        if (!this.senderName().trim()) blockers.push('label.missing_element_mail_empty_name');
        if (!this.senderLocalPart().trim()) blockers.push('label.missing_element_mail_empty_mail');
        if (!this.senderDomain().trim()) blockers.push('label.missing_element_mail_sender_domain');
        if (!this.object().trim()) blockers.push('label.missing_element_mail_empty_object');
        // Sans bouton, l'e-mail part sans emporter le questionnaire : il ne collecterait rien.
        if (!this.hasCollectButton()) blockers.push('label.missing_element_mail_no_button');
        // Un emplacement d'image resté vide partirait au destinataire en cadre vide.
        if (this.content().hasEmptyImage()) blockers.push('label.missing_element_mail_no_image_id');

        return blockers;
    });

    public readonly canPublish = computed<boolean>(() => this.publicationBlockers().length === 0);

    constructor() {
        this.draftToSave
            .pipe(
                debounceTime(SAVE_DRAFT_DEBOUNCE_MS),
                tap(() => {
                    this.savingSignal.set(true);
                    this.saveFailedSignal.set(false);
                }),
                // `switchMap` : seul le dernier état du média compte, un enregistrement en cours
                // devient caduc dès qu'une nouvelle modification arrive.
                switchMap((sent) =>
                    this.mediaService.saveDraft(sent).pipe(
                        map((saved) => ({sent, saved})),
                        catchError(() => {
                            this.savingSignal.set(false);
                            this.saveFailedSignal.set(true);
                            // L'erreur ne doit pas rompre le flux : les modifications suivantes
                            // doivent continuer à être enregistrées.
                            return EMPTY;
                        })
                    )
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(({sent, saved}) => {
                this.adoptSavedDraft(sent, saved);
                this.savingSignal.set(false);
            });
    }

    /**
     * Dépose le média à éditer, et ouvre la session sur ses paramètres d'envoi.
     *
     * La sélection et l'historique n'ont de sens que pour le média qui les a vus naître : la route
     * étant la même pour tous, le store peut survivre à la navigation — c'est donc ici, au dépôt,
     * que la session repart de zéro.
     */
    public readonly setMediaEmail = (
        mediaEmail: MediaEmail,
        publishedVersion: MediaEmail | null = null,
        surveyHeader: SurveyHeader | null = null
    ): void => {
        this.surveyHeaderSignal.set(surveyHeader);
        // Le serveur crée l'e-mail à partir d'un gabarit dont le corps, le libellé du bouton et le
        // lien de désabonnement sont des clés : elles deviennent du texte ici, une fois pour toutes.
        // Écrit sans passer par `replaceMediaEmail` : ce n'est pas une modification de
        // l'utilisateur, elle n'a rien à faire dans l'historique.
        const translated = mediaEmail.withTranslatedTexts(this.translateLabel);
        this.mediaEmailSignal.set(translated);
        this.publishedVersionSignal.set(publishedVersion);
        this.selectionSignal.set(SENDING_SELECTION);
        // Une question laissée en suspens portait sur le média précédent : au rang qu'elle désigne,
        // celui-ci a tout autre chose.
        this.pendingDeletionSignal.set(null);
        this.pastSignal.set([]);
        this.futureSignal.set([]);
        this.lastCoalesceKey = null;
        this.stateRevisionSignal.update((revision) => revision + 1);

        // La traduction des clés est enregistrée aussitôt : sans cela le serveur garderait ses clés
        // d'un e-mail auquel l'utilisateur n'aurait rien touché, et le message partirait en clés.
        // Rien n'est envoyé quand il n'y avait rien à traduire — rouvrir un e-mail déjà corrigé
        // n'enregistre alors rien.
        if (translated !== mediaEmail) this.draftToSave.next(translated);
    };

    public readonly select = (selection: MailBuilderSelection): void => this.selectionSignal.set(selection);

    /** Retour aux paramètres d'envoi : c'est ce que règle le panneau quand rien n'est sélectionné. */
    public readonly selectSending = (): void => this.selectionSignal.set(SENDING_SELECTION);

    /**
     * Désigne une section. La section du lien de désabonnement fait exception : elle est imposée
     * par la loi, il n'y a rien à y régler.
     */
    public readonly selectRow = (rowIndex: number): void => {
        if (this.rows()[rowIndex]?.isUnsubscribe()) return;

        this.selectionSignal.set({kind: 'row', rowIndex});
    };

    public readonly selectBlock = (path: EmailBlockPath): void => this.selectionSignal.set({kind: 'block', ...path});

    public readonly updateSenderName = (senderName: string): void =>
        this.replaceMediaEmail((mediaEmail) => mediaEmail.withSenderName(senderName), 'sender-name');

    public readonly updateSenderAddress = (localPart: string, domain: string): void =>
        this.replaceMediaEmail((mediaEmail) => mediaEmail.withSenderAddress(localPart, domain), 'sender-address');

    public readonly updateObject = (object: string): void =>
        this.replaceMediaEmail((mediaEmail) => mediaEmail.withObject(object), 'object');

    public readonly updateBodyColor = (bodyColor: string): void =>
        this.replaceContent((content) => content.withBodyColor(bodyColor), 'body-color');

    /**
     * Dépose une section au rang voulu et s'y installe : on vient de l'ajouter, c'est elle que l'on
     * veut régler. Elle arrive sur fond blanc, comme celles du gabarit — le fond du corps est ce
     * sur quoi elle se pose, s'en revêtir la ferait disparaître.
     */
    public readonly addRow = (type: EMAIL_ROW_TYPE, index: number): void => {
        const row = buildEmailRow(type);
        this.replaceContent((content) => content.withRowAdded(row, index));
        this.selectRow(this.rows().indexOf(row));
    };

    /**
     * Déplace une section. Elle garde son objet en changeant de rang : l'éditeur continue de la
     * reconnaître, et la sélection la suit plutôt que de rester sur son ancien rang.
     */
    public readonly moveRow = (from: number, to: number): void => {
        if (!this.rows()[from]) return;

        this.replaceContent((content) => content.withRowMoved(from, to));
        this.selectionSignal.update((selection) => afterRowMove(selection, from, to));
    };

    /**
     * Copie une section juste sous elle et s'y installe : la copie est ce que l'on vient d'obtenir,
     * c'est elle que l'on va retoucher — sans quoi le panneau réglerait encore l'originale.
     *
     * Celle du lien de désabonnement fait exception, comme partout : l'e-mail n'en porte qu'une.
     */
    public readonly duplicateRow = (index: number): void => {
        const row = this.rows()[index];
        if (!row || row.isUnsubscribe()) return;

        this.replaceContent((content) => content.withRowAdded(row.copy(), index + 1));
        this.selectRow(index + 1);
    };

    /**
     * Retire une section, et tout ce qu'elle portait. Celle du lien de désabonnement fait
     * exception : elle est imposée par la loi, et l'e-mail ne partirait plus sans elle.
     */
    public readonly deleteRow = (index: number): void => {
        const row = this.rows()[index];
        if (!row || row.isUnsubscribe()) return;

        this.replaceContent((content) => content.withRowRemoved(index));
        this.selectionSignal.update((selection) => afterRowDeletion(selection, index));
    };

    /**
     * Change le découpage en colonnes d'une section.
     *
     * La sélection revient sur la section : un bloc désigné pouvait vivre dans une colonne que le
     * nouveau découpage n'a plus, et son chemin ne veut alors plus rien dire.
     */
    public readonly updateRowType = (index: number, type: EMAIL_ROW_TYPE): void => {
        this.replaceRow(index, (row) => row.withType(type));
        this.selectRow(index);
    };

    public readonly updateRowColumnWidths = (index: number, widths: number[]): void =>
        this.replaceRow(index, (row) => row.withColumnWidths(widths), `row-widths-${index}`);

    public readonly updateRowOptions = (index: number, patch: Partial<EmailRowOptions>): void =>
        this.replaceRow(index, (row) => row.withOptions(patch), `row-options-${index}-${Object.keys(patch).join()}`);

    /**
     * Dépose un bloc dans une colonne et le désigne : on vient de l'ajouter, c'est lui que l'on
     * veut régler.
     */
    public readonly addBlock = (type: EmailBlockCard['type'], path: EmailBlockPath): void => {
        const block = buildEmailBlock(type, this.translateLabel);

        this.replaceColumn(path, (column) => column.withBlockAdded(block, path.blockIndex));

        const column = this.rows()[path.rowIndex]?.columns[path.columnIndex];
        if (column) this.selectBlock({...path, blockIndex: column.content.indexOf(block)});
    };

    /**
     * Déplace un bloc, dans sa colonne ou vers une autre. La sélection suit le bloc jusqu'à son
     * nouvel emplacement : c'est celui que l'utilisateur vient de choisir.
     */
    public readonly moveBlock = (from: EmailBlockPath, to: EmailBlockPath): void => {
        const block = this.content().blockAt(from.rowIndex, from.columnIndex, from.blockIndex);
        if (!block) return;

        if (from.rowIndex === to.rowIndex && from.columnIndex === to.columnIndex) {
            this.replaceColumn(from, (column) => column.withBlockMoved(from.blockIndex, to.blockIndex));
            this.selectBlock(to);
            return;
        }

        // D'une colonne à l'autre : le retrait d'abord, le dépôt ensuite sur le contenu qu'il
        // laisse — sans quoi le second travaillerait sur un contenu que le premier a périmé. Les
        // deux portent la même clé : c'est un seul geste, il ne s'annule pas en deux fois.
        const step = `block-move-${this.identityOf(block, block.type)}`;
        this.replaceColumn(from, (column) => column.withBlockRemoved(from.blockIndex), step);
        this.replaceColumn(to, (column) => column.withBlockAdded(block, to.blockIndex), step);
        this.selectBlock(to);
    };

    public readonly deleteBlock = (path: EmailBlockPath): void => {
        if (!this.content().blockAt(path.rowIndex, path.columnIndex, path.blockIndex)) return;

        this.replaceColumn(path, (column) => column.withBlockRemoved(path.blockIndex));
        this.selectionSignal.update((selection) => afterBlockDeletion(selection, path));
    };

    /**
     * Retouche les réglages d'un bloc, sans toucher au reste de ce qu'il porte.
     *
     * Le type est reconduit tel quel : `patch` ne porte que des options, un bloc ne change jamais
     * de nature. Les retouches successives d'un même bloc ne font qu'un pas d'annulation.
     */
    public readonly updateBlockOptions = <T extends EmailBlock>(
        path: EmailBlockPath,
        patch: Partial<T['options']>
    ): void => {
        const current = this.content().blockAt(path.rowIndex, path.columnIndex, path.blockIndex);
        if (!current) return;

        const updated = {...current, options: {...current.options, ...patch}} as EmailBlock;
        this.inheritIdentity(current, updated);

        this.replaceColumn(
            path,
            (column) => column.withBlock(path.blockIndex, updated),
            `block-options-${path.rowIndex}-${path.columnIndex}-${path.blockIndex}-${Object.keys(patch).join()}`
        );
    };

    /**
     * Retire ce que le panneau de réglages a sous la main : une section, ou un bloc.
     *
     * Le panneau n'a pas à savoir lequel des deux il règle : ce qu'il montre suit la sélection, sa
     * suppression aussi. Rien à retirer quand ce sont les paramètres d'envoi qui sont affichés.
     */
    public readonly deleteSelection = (): void => {
        const selection = this.selectionSignal();

        if (selection.kind === 'row') this.deleteRow(selection.rowIndex);
        if (selection.kind === 'block') this.deleteBlock(selection);
    };

    /**
     * Demande confirmation avant de retirer une section.
     *
     * Rien n'est demandé pour celle du lien de désabonnement : elle ne se supprime pas, et une
     * question à laquelle répondre « oui » ne ferait rien serait pire que pas de question du tout.
     */
    public readonly askDeleteRow = (rowIndex: number): void => {
        const row = this.rows()[rowIndex];
        if (!row || row.isUnsubscribe()) return;

        this.pendingDeletionSignal.set({kind: 'row', rowIndex});
    };

    /** Idem depuis le panneau de réglages, qui retire ce qu'il règle — une section ou un bloc. */
    public readonly askDeleteSelection = (): void => {
        const selection = this.selectionSignal();
        if (selection.kind === 'sending') return;

        if (selection.kind === 'row') {
            this.askDeleteRow(selection.rowIndex);
            return;
        }

        this.pendingDeletionSignal.set(selection);
    };

    /** Referme la question sans rien retirer. */
    public readonly cancelDeletion = (): void => this.pendingDeletionSignal.set(null);

    /**
     * Retire ce dont la suppression était en attente.
     *
     * La question se referme d'abord : ce qui est retiré n'existe plus au rang que la question
     * désignait, et la rouvrir sur ce rang parlerait de la section suivante.
     */
    public readonly confirmDeletion = (): void => {
        const pending = this.pendingDeletionSignal();
        if (!pending) return;

        this.pendingDeletionSignal.set(null);

        if (pending.kind === 'row') this.deleteRow(pending.rowIndex);
        else this.deleteBlock(pending);
    };

    /**
     * Revient d'un pas en arrière.
     *
     * La sélection retombe sur les paramètres d'envoi : l'état retrouvé peut ne plus porter ce
     * qu'elle désignait, et un panneau ouvert sur un bloc disparu ne réglerait rien.
     */
    public readonly undo = (): void => {
        const past = this.pastSignal();
        const current = this.mediaEmailSignal();
        const previous = past[past.length - 1];
        if (!current || !previous) return;

        this.pastSignal.set(past.slice(0, -1));
        this.futureSignal.update((future) => [current, ...future]);
        this.adoptRestoredState(previous);
    };

    public readonly redo = (): void => {
        const [next, ...rest] = this.futureSignal();
        const current = this.mediaEmailSignal();
        if (!current || !next) return;

        this.futureSignal.set(rest);
        this.pastSignal.update((past) => [...past, current]);
        this.adoptRestoredState(next);
    };

    /**
     * Identité de ce que le canevas affiche, telle qu'il le suit d'une version du média à la
     * suivante.
     *
     * Elle ne peut pas vivre dans le modèle : tout ce que le contenu porte repart au serveur, et
     * une clé de l'éditeur n'a rien à y faire. Elle ne peut pas non plus se déduire de l'objet —
     * deux espaces de même hauteur sont identiques en tout point. Elle est donc tenue à côté,
     * attachée à l'objet lui-même.
     */
    private readonly identities = new WeakMap<object, string>();

    private identityCount = 0;

    /** Identité d'une section, attribuée à la première rencontre et conservée ensuite. */
    public readonly rowIdentity = (row: EmailRow): string => this.identityOf(row, 'ROW');

    /** Identité d'un bloc. Le type la compose pour qu'elle se lise : `TEXT#3`. */
    public readonly blockIdentity = (block: EmailBlock): string => this.identityOf(block, block.type);

    private readonly identityOf = (target: object, prefix: string): string => {
        const known = this.identities.get(target);
        if (known) return known;

        const identity = `${prefix}#${this.identityCount++}`;
        this.identities.set(target, identity);

        return identity;
    };

    private readonly inheritIdentity = (from: object, to: object): void => {
        const identity = this.identities.get(from);
        if (identity) this.identities.set(to, identity);
    };

    /**
     * Remplace une colonne par elle-même, retouchée, et reporte les identités de tout ce que le
     * remplacement reconstruit au-dessus d'elle.
     *
     * Une colonne qui change fabrique une nouvelle section, qui fabrique un nouveau contenu : sans
     * ce report, l'éditeur ne reconnaîtrait plus la section et referait tout ce qu'elle porte —
     * emportant le curseur de qui serait en train d'y écrire.
     */
    private readonly replaceColumn = (
        path: EmailBlockPath,
        update: (column: EmailColumn) => EmailColumn,
        coalesceKey?: string
    ): void => {
        const column = this.rows()[path.rowIndex]?.columns[path.columnIndex];
        if (!column) return;

        this.replaceRow(path.rowIndex, (row) => row.withColumn(path.columnIndex, update(column)), coalesceKey);
    };

    /** Remplace une section par elle-même, retouchée ; son identité la suit. */
    private readonly replaceRow = (index: number, update: (row: EmailRow) => EmailRow, coalesceKey?: string): void => {
        const row = this.rows()[index];
        if (!row) return;

        const updated = update(row);
        this.inheritIdentity(row, updated);

        this.replaceContent((content) => content.withRow(index, updated), coalesceKey);
    };

    private readonly replaceContent = (update: (content: EmailContent) => EmailContent, coalesceKey?: string): void =>
        this.replaceMediaEmail((mediaEmail) => mediaEmail.withContent(update(mediaEmail.content)), coalesceKey);

    /**
     * Le seul chemin par lequel le média change.
     *
     * L'historique s'y empile, et ce qui aurait pu être rétabli s'y annule : repartir dans une
     * autre direction après une annulation ferme l'avenir qu'on venait de quitter. C'est aussi d'ici
     * que part l'enregistrement différé.
     */
    private readonly replaceMediaEmail = (
        update: (mediaEmail: MediaEmail) => MediaEmail,
        coalesceKey?: string
    ): void => {
        const current = this.mediaEmailSignal();
        if (!current) return;

        if (coalesceKey === undefined || coalesceKey !== this.lastCoalesceKey) {
            this.pastSignal.update((past) => [...past, current].slice(-HISTORY_DEPTH));
        }

        this.lastCoalesceKey = coalesceKey ?? null;
        this.futureSignal.set([]);

        const next = update(current);
        this.mediaEmailSignal.set(next);
        this.draftToSave.next(next);
    };

    /**
     * Reprend le média que le serveur renvoie : il est le même, à ceci près qu'il sait si une
     * version de production existe désormais.
     *
     * Seul ce que le serveur tient est repris — le contenu reste celui de l'éditeur, qui a pu
     * changer depuis l'envoi. La reprise n'a lieu que si rien n'a bougé entre-temps ; l'égalité de
     * référence suffit à le dire, toute modification remplaçant le média par une nouvelle instance.
     *
     * Écriture directe du signal, sans repasser par `replaceMediaEmail` : réenregistrer ce que le
     * serveur vient de renvoyer relancerait la boucle sans fin.
     */
    private readonly adoptSavedDraft = (sent: MediaEmail, saved: MediaEmail): void => {
        if (this.mediaEmailSignal() !== sent) return;

        this.mediaEmailSignal.set(new MediaEmail({...sent, prodVersion: saved.prodVersion}));
    };

    /**
     * Reprend un état de l'historique. La suite des retouches repart à zéro : ce qui vient d'être
     * rétabli ne se fond pas avec la modification suivante.
     */
    private readonly adoptRestoredState = (mediaEmail: MediaEmail): void => {
        this.mediaEmailSignal.set(mediaEmail);
        this.selectionSignal.set(SENDING_SELECTION);
        this.lastCoalesceKey = null;
        // L'état vient d'ailleurs que des panneaux : ils doivent réamorcer leurs formulaires.
        this.stateRevisionSignal.update((revision) => revision + 1);
    };
}
